import React, { FC } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import {
  useModelFilterActions,
  useModelFilters,
} from '@store/zustand/model-filters';
import type { CiaFilterCategory } from '@utils/modelFilterCounts';
import {
  CATEGORY_LABELS,
  CategoryCriteria,
} from '@views/Project/components/ModelFiltersPopover/categories';
import FilterCategoryRow from '@views/Project/components/ModelFiltersPopover/components/FilterCategoryRow';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';

type Props = {
  category: CiaFilterCategory;
  criteria: CategoryCriteria[];
  activeCriteriaId?: string;
  onOpenCriteria: (criteriaId: string) => void;
  onBack: () => void;
};

/**
 * The criteria list of a CIA category with more than one criteria: the
 * category's own panel, headed by the category's default name.
 *
 * Assurance, Coverage and Applicability are configuration-driven: each is a
 * list of criteria and the unit a user selects by is one criteria's answer.
 * A category with a single criteria skips this list and opens its values
 * directly; with several, each row here opens that criteria's values in a
 * panel beside this list (AC3.1). The list decides nothing about that
 * panel: every click reports the criteria, the active row's included, and
 * the container opens, swaps or closes the side panel from it, marking the
 * open row through `activeCriteriaId`. Each row carries the AC3.2 summary
 * of its own answers and, in place of the root row's dot, a badge with the
 * number of answers ticked under it (AC3.3). "Clear all" appears while any
 * criteria of the category has a selection and empties the whole category,
 * which is AC5.2 at the level this header names.
 */
const FilterCriteriaPanel: FC<Props> = ({
  category,
  criteria,
  activeCriteriaId,
  onOpenCriteria,
  onBack,
}) => {
  const { ciaFilters } = useModelFilters();
  const { clearCategory } = useModelFilterActions();

  const selection = ciaFilters[category];
  const hasSelection = Object.values(selection).some(
    (answers) => answers.length > 0,
  );

  return (
    <FilterPanelLayout
      title={
        <FilterPanelBackTitle
          title={CATEGORY_LABELS[category]}
          onBack={onBack}
        />
      }
      action={
        hasSelection && (
          <UnderlineTextButton
            $disabled={false}
            onClick={() => clearCategory(category)}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {criteria.map(({ id, name }) => (
        <FilterCategoryRow
          key={id}
          icon={null}
          label={name}
          values={selection[id] ?? []}
          labels={{}}
          badge={selection[id]?.length ?? 0}
          selected={id === activeCriteriaId}
          onClick={() => onOpenCriteria(id)}
        />
      ))}
    </FilterPanelLayout>
  );
};

export default FilterCriteriaPanel;
